import { c as _c } from "react/compiler-runtime";
import { FetcherProvider } from "@website/fetcher/provider";
import { useAuthPages } from "@website/hooks/auth-manager";
import { LocalStorageProvider } from "@website/hooks/local-storage";
import { startUserTracking } from "@website/tracking/user";
import { SnackbarProvider } from "@website/ui/snackbar";
import "@website/ui/styles";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import { InjectExperimentConfigScript, configureExperimentServerSide, type GBFeatures } from "@website/utils/experiments/server";
import { SERVER_NOW } from "@website/utils/server-now";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { ChatBotExperimentModalHost } from "@website/widget/chat-bot";
import { Scripts } from "@website/widget/scripts/scripts";
import { WebVitals } from "@website/widget/scripts/web-vitals";
import NextApp, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { Layout } from "../components";
startUserTracking({
  product: "core"
});
type ProfilePageProperties = {
  gbFeatures?: GBFeatures;
  seoData?: {
    robots?: string;
  };
  serverNow?: string;
};
const MyApp = t0 => {
  const $ = _c(24);
  const {
    Component,
    pageProps
  } = t0;
  useAuthPages();
  const {
    gbFeatures
  } = pageProps as ProfilePageProperties;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      android: "11b5650c0",
      ios: "11b5650c0",
      jek: "~47b6574d",
      ap: "11b5650c0",
      app: "11b5650c0",
      ads: "11b5650c0",
      "snapp-ir": "~47b6574d",
      snapp: "~47b6574d",
      website: "11b5650c0",
      telesale: "11b5650c0"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const webEngageLicenseCode = t1[getChannel() as Channels];
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      android: "GTM-MT54VMN",
      ios: "GTM-KJPQFH4",
      jek: "GTM-P8WGVRK",
      ap: "GTM-KJPQFH4",
      app: "GTM-KJPQFH4",
      ads: "GTM-KJPQFH4",
      "snapp-ir": "GTM-5HTF8BD",
      snapp: "GTM-P8WGVRK",
      website: "GTM-MT54VMN",
      telesale: "GTM-MT54VMN"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const gtmLicenseCode = t2[getChannel() as Channels];
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <title>{replaceSnappTripBasedOnChannel("SnappTrip Profile")}</title>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== pageProps.seoData?.robots) {
    t4 = pageProps?.seoData?.robots ? <meta content={pageProps?.seoData?.robots} name="robots" /> : <meta content="noindex,nofollow" name="robots" />;
    $[3] = pageProps.seoData?.robots;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <meta content={replaceSnappTripBasedOnChannel("SnappTrip dashboard")} name="description" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== gbFeatures) {
    t6 = gbFeatures && <InjectExperimentConfigScript features={gbFeatures} />;
    $[6] = gbFeatures;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <Head>{t3}{t4}{t5}{t6}</Head>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== Component || $[12] !== pageProps) {
    t8 = <Layout><Component {...pageProps} /></Layout>;
    $[11] = Component;
    $[12] = pageProps;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChatBotExperimentModalHost />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8) {
    t10 = <SnackbarProvider><FetcherProvider><LocalStorageProvider prefix="profile" version={1}>{t8}{t9}</LocalStorageProvider></FetcherProvider></SnackbarProvider>;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== pageProps.serverNow) {
    t11 = <Scripts gtmLicence={gtmLicenseCode} serverNow={pageProps.serverNow} webengageLicence={webEngageLicenseCode} />;
    $[17] = pageProps.serverNow;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <WebVitals />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t7) {
    t13 = <>{t7}{t10}{t11}{t12}</>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t7;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
const getAppInitialProperties = async (appContext: AppContext) => {
  const appProperties = await NextApp.getInitialProps(appContext);
  const gbFeatures = appContext.ctx.req ? await configureExperimentServerSide() : undefined;
  return {
    ...appProperties,
    pageProps: {
      ...appProperties.pageProps,
      serverNow: SERVER_NOW(),
      ...(gbFeatures ? {
        gbFeatures
      } : {})
    }
  };
};
MyApp.getInitialProps = getAppInitialProperties;
export default MyApp;